import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import WebRTCService from '@/services/WebRTCService';
import NotificationService from '@/services/NotificationService';

export function useCallManager() {
  const store = useStore();
  
  // Reactive state
  const inSession = ref(false);
  const phoneConnected = ref(false);
  const newCall = ref(null);
  const durationTimer = ref(null);
  const isMinimised = ref(false);
  const isAdminCall = ref(false);
  const isInitializing = ref(false);

  // Computed properties
  const sessionPeer = computed(() => {
    return WebRTCService.getSessionPeer();
  });

  const showDial = computed(() => {
    return !inSession.value;
  });

  const enableDial = computed(() => {
    const numberText = store.getters.numberToCall;
    return numberText && !isNaN(numberText) && numberText.length >= 3;
  });

  // Methods
  const initPhone = async () => {
    const status = store.getters.status;
    const domain = store.getters.domain;
    const phonePermission = store.getters.phonePermission;
    const phoneInfo = store.getters.phoneInfo;

    if (status === 4) return;

    // Prevent multiple simultaneous initializations
    if (isInitializing.value) {
      console.log('Phone initialization already in progress, skipping...');
      return;
    }

    // Don't reinitialize if already connected
    if (WebRTCService.isConnected()) {
      console.log('Phone already connected, skipping initialization');
      return;
    }

    // Check if WebSocket is already connecting
    if (WebRTCService.isWebSocketConnected()) {
      console.log('WebSocket already connected, skipping initialization');
      return;
    }

    isInitializing.value = true;

    try {
      if (domain) {
        await getCalls();
      }

      if (domain && hasPermission(phonePermission)) {
        WebRTCService.initPhoneConfig(phoneInfo);
        setupWebRTCCallbacks(); // Always set up callbacks
        
        // Only create new session if not connected
        if (!WebRTCService.isConnected()) {
          WebRTCService.createSession(phoneInfo);
        }
      }
    } finally {
      // Reset flag after a short delay to allow connection to establish
      setTimeout(() => {
        isInitializing.value = false;
      }, 2000);
    }
  };

  const setupWebRTCCallbacks = () => {
    WebRTCService.setCallbacks({
      onConnected: () => {
        // Only set connected when both WebSocket and SIP registration are successful
        console.log('WebSocket connected, waiting for SIP registration...');
        // Don't set isConnected yet - wait for registration
      },
      onDisconnected: () => {
        phoneConnected.value = false;
        store.commit('setIsConnected', false);
        isInitializing.value = false; // Reset flag on disconnect to allow retry
      },
      onRegistered: () => {
        // Now both WebSocket and SIP are ready
        console.log('SIP registration successful - setting connected status');
        phoneConnected.value = true;
        store.commit('setIsConnected', true);
        isInitializing.value = false; // Reset initialization flag when connected
      },
      onUnregistered: () => {
        // Handle unregistration
        phoneConnected.value = false;
        store.commit('setIsConnected', false);
      },
      onRegistrationFailed: (e) => {
        console.log('Registration failed:', e);
        // Ensure we're not showing as connected if registration fails
        phoneConnected.value = false;
        store.commit('setIsConnected', false);
        isInitializing.value = false; // Reset flag on failure to allow retry
      },
      onConnectionLost: () => {
        // Handle connection loss detected during page visibility changes
        console.log('Connection lost detected - reinitializing phone');
        phoneConnected.value = false;
        store.commit('setIsConnected', false);
        // Reinitialize phone after a short delay
        setTimeout(() => {
          initPhone();
        }, 1000);
      },
      onNewSession: () => {
        inSession.value = true;
        store.commit('setIncall', true); // Set inCall directly here
        
        // Set session peer immediately when session starts
        const peer = WebRTCService.getSessionPeer();
        console.log('onNewSession - setting session peer:', peer);
        if (peer?.includes('asterisk')) {
          store.commit('setSessionPeer', 'Call Monitor');
        } else {
          store.commit('setSessionPeer', peer);
        }
        getCurrentPBXCall();
        // The queue's display name comes from domainSkills, which is otherwise
        // only fetched when a page that needs it is created — so a call can ring
        // before it exists. Fetch it here so the queue badge can name the queue
        // rather than show its number.
        if (!store.getters.domainSkills) {
          store.dispatch('GetDomainSkills');
        }
      },
      onSessionEnded: (e) => {
        console.log('session ended', e);
        inSession.value = false;
        stopWhisperIfActive();
        store.commit('setIncall', false); // Set inCall directly here
        handleSessionEnded();
        resetCallState(); // Reset call state directly here
      },
      onSessionProgress: (e) => {
        console.log('session progress', e);
        // Auto-answer is now handled by WebRTCService based on configuration
      },
      onSessionHold: (e) => {
        console.log('session hold', e);
        store.commit('setIsHolding', true);
      },
      onSessionUnhold: (e) => {
        console.log('session unhold', e);
        store.commit('setIsHolding', false);
      },
      onSessionMuted: (e) => {
        console.log('session muted', e);
        store.commit('setIsMute', true);
      },
      onSessionUnmuted: (e) => {
        console.log('session unmuted', e);
        store.commit('setIsMute', false);
      },
      onSessionFailed: (e) => {
        console.log('session failed', e);
        inSession.value = false;
        store.commit('setIncall', false); // Set inCall directly here
        handleSessionFailed();
        resetCallState(); // Reset call state directly here
      },
      onSessionAccepted: (e) => {
        console.log('session accepted', e);
        handleSessionAccepted();
      },
      onSessionConfirmed: (e) => {
        console.log('session confirmed', e);
        getCurrentPBXCall()
        handleSessionConfirmed();
      },
      onSessionIncoming: () => {
        store.commit('setIsIncoming', true);

        // Defensive: Ensure inCall is set even if onNewSession didn't fire
        if (!store.getters.inCall) {
          console.log('onSessionIncoming - setting inCall as defensive measure');
          store.commit('setIncall', true);
          inSession.value = true;
        }

        // Set session peer for incoming calls
        const peer = WebRTCService.getSessionPeer();
        console.log('onSessionIncoming - setting session peer:', peer);
        const displayPeer = peer?.includes('asterisk') ? 'Call Monitor' : peer;
        store.commit('setSessionPeer', displayPeer);

        // Desktop (Electron): hand the incoming call to the main process, which
        // can un-minimize + focus the window and post a native OS notification
        // (the renderer can do neither reliably). On the web, fall back to the
        // browser Notification API.
        if (window.desktop && window.desktop.incomingCall) {
          window.desktop.incomingCall({ caller: displayPeer });
        } else {
          NotificationService.notifyIncomingCall({ caller: displayPeer });
        }
      },
      onSessionOutgoing: () => {
        store.commit('setIsOutgoing', true);
        
        // Defensive: Ensure inCall is set even if onNewSession didn't fire
        if (!store.getters.inCall) {
          console.log('onSessionOutgoing - setting inCall as defensive measure');
          store.commit('setIncall', true);
          inSession.value = true;
        }
        
        // Set session peer for outgoing calls
        const peer = WebRTCService.getSessionPeer();
        console.log('onSessionOutgoing - setting session peer:', peer);
        if (peer?.includes('asterisk')) {
          store.commit('setSessionPeer', 'Call Monitor');
        } else {
          store.commit('setSessionPeer', peer);
        }
      },
      onIncomingStream: (e) => {
        handleIncomingStream(e);
      },
      onOutgoingStream: (e) => {
        handleOutgoingStream(e);
      },
      onPeerConnectionTrack: (e) => {
        handlePeerConnectionTrack(e);
      }
    });
  };

  const handleSessionEnded = () => {
    if (newCall.value) {
      if (!sessionPeer.value?.includes('asterisk')) {
        store.commit('setCallsList', [newCall.value, ...store.getters.callsList]);
      }
      newCall.value = null;
    }
    clearInterval(durationTimer.value);
  };

  const handleSessionFailed = () => {
    if (newCall.value) {
      const direction = WebRTCService.getSessionDirection();
      if (direction === 'incoming') {
        newCall.value.call_type = 1;
      }
      if (newCall.value.call_type === 1) {
        newCall.value.status = 'Drop';
      } else {
        newCall.value.status = 'NOT ANSWERED';
      }
      if (!sessionPeer.value?.includes('asterisk')) {
        store.commit('setCallsList', [newCall.value, ...store.getters.callsList]);
      }
      newCall.value = null;
    }
  };

  const handleSessionAccepted = () => {
    // Defensive: Ensure inCall is set when call is accepted
    if (!store.getters.inCall) {
      console.log('handleSessionAccepted - setting inCall as defensive measure');
      store.commit('setIncall', true);
      inSession.value = true;
    }
    
    // Check if this is an admin call (user doesn't have agentStatusPermission)
    const agentStatusPermission = store.getters.agentStatusPermission;
    if (!hasPermission(agentStatusPermission)) {
      isAdminCall.value = true;
      isMinimised.value = true;
    }
    
    const callStart = moment(new Date(WebRTCService.getSessionStartTime())).unix();
    const currentCall = store.getters.currentCall;
    
    if (currentCall) {
      store.commit('setCurrentCall', {
        ...currentCall,
        Start: callStart,
        isAnswered: true,
      });
    } else {
      store.commit('setCurrentCall', { Start: callStart, isAnswered: true });
    }
    
    store.commit('setIsAnswered', true);
    setCallDuration();
    
    if (newCall.value) {
      const direction = WebRTCService.getSessionDirection();
      if (direction === 'incoming') {
        newCall.value.call_type = 1;
      }
      newCall.value.status = 'ANSWERED';
    } else {
      const direction = WebRTCService.getSessionDirection();
      if (direction === 'incoming') {
        newCall.value = {
          call_type: 1,
          status: 'ANSWERED',
          number: sessionPeer.value,
          time: moment().format('YYYY-MM-DD HH:mm:ss'),
          duration: '00:00:00',
        };
      }
    }
  };

  const handleSessionConfirmed = () => {
    if (newCall.value) {
      const direction = WebRTCService.getSessionDirection();
      if (direction === 'incoming') {
        newCall.value.call_type = 1;
      }
      newCall.value.status = 'ANSWERED';
    } else {
      const direction = WebRTCService.getSessionDirection();
      if (direction === 'incoming') {
        newCall.value = {
          call_type: 1,
          status: 'ANSWERED',
          number: sessionPeer.value,
          time: moment().format('YYYY-MM-DD HH:mm:ss'),
          duration: '00:00:00',
        };
      }
    }

    if (sessionPeer.value?.includes('asterisk')) {
      store.commit('setSessionPeer', 'Call Monitor');
    } else {
      store.commit('setSessionPeer', sessionPeer.value);
    }
  };

  const handleIncomingStream = (e) => {
    console.log('handleIncomingStream called', e);
    console.log('Stream from event:', e.stream);
    console.log('Stream tracks:', e.stream ? e.stream.getTracks() : 'No stream');
    
    // Handle incoming audio stream with improved error handling
    setupAudioStream(e.stream, 'incoming');
  };

  const handleOutgoingStream = (e) => {
    // Handle outgoing audio stream with improved error handling
    setupAudioStream(e.stream, 'outgoing');
  };

  const handlePeerConnectionTrack = (e) => {
    console.log('handlePeerConnectionTrack called', e);
    console.log('Stream from ontrack event:', e.stream);
    console.log('Streams from ontrack event:', e.streams);
    console.log('Track from ontrack event:', e.track);
    
    // Handle peer connection track with improved error handling
    let streamToUse = e.stream;
    if (!streamToUse && e.streams && e.streams.length > 0) {
      streamToUse = e.streams[0];
      console.log('Using stream from streams array');
    } else if (!streamToUse && WebRTCService.pcStream) {
      streamToUse = WebRTCService.pcStream;
      console.log('Using stream from WebRTCService.pcStream');
    }
    
    if (streamToUse) {
      setupAudioStream(streamToUse, 'peer');
    } else {
      console.log('No stream available to set');
    }
  };

  // Improved audio stream setup with fallback mechanisms
  const setupAudioStream = (stream, source) => {
    const audioElement = document.getElementById('remote-stream-audio');
    
    if (!audioElement) {
      console.log('Audio element not found, creating one');
      createAudioElement();
      return;
    }
    
    console.log(`Setting up audio stream from ${source}`);
    
    // Pause current audio if playing
    if (!audioElement.paused) {
      audioElement.pause();
    }
    
    // Set the new stream
    audioElement.srcObject = stream;
    
    // Attempt to play with multiple retries
    playAudioWithRetry(audioElement, source);
  };

  // Create audio element if it doesn't exist
  const createAudioElement = () => {
    const existingElement = document.getElementById('remote-stream-audio');
    if (existingElement) {
      return existingElement;
    }
    
    const audioElement = document.createElement('audio');
    audioElement.id = 'remote-stream-audio';
    audioElement.autoplay = true;
    audioElement.controls = false;
    document.body.appendChild(audioElement);
    
    console.log('Created new audio element');
    return audioElement;
  };

  // Play audio with retry mechanism
  const playAudioWithRetry = (audioElement, source, retryCount = 0) => {
    const maxRetries = 3;
    
    // First check if audio context needs to be resumed
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      try {
        const audioContext = new AudioContext();
        if (audioContext.state === 'suspended') {
          console.log(`Audio context suspended for ${source} - attempting resume`);
          audioContext.resume().then(() => {
            console.log('Audio context resumed - retrying playback');
            playAudioWithRetry(audioElement, source, retryCount);
          }).catch((error) => {
            console.log('Failed to resume audio context:', error);
          });
          return;
        }
      } catch (error) {
        console.log('Error checking audio context:', error);
      }
    }
    
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log(`${source} audio play promise fulfilled`);
          WebRTCService.icomingProFulfiled = true;
        })
        .catch((error) => {
          console.log(`Error in ${source} play stream (attempt ${retryCount + 1}):`, error);
          
          // Handle different types of errors
          if (error.name === 'NotAllowedError') {
            console.log(`${source} autoplay blocked - requires user interaction`);
            // Add click listener to resume on user interaction
            const resumeOnClick = () => {
              audioElement.play().then(() => {
                console.log(`${source} audio resumed after user interaction`);
                WebRTCService.icomingProFulfiled = true;
                document.removeEventListener('click', resumeOnClick);
              }).catch((err) => {
                console.log(`${source} audio still failed after user interaction:`, err);
              });
            };
            document.addEventListener('click', resumeOnClick, { once: true });
          } else if (retryCount < maxRetries) {
            console.log(`Retrying ${source} audio playback in 1 second...`);
            setTimeout(() => {
              playAudioWithRetry(audioElement, source, retryCount + 1);
            }, 1000);
          } else {
            console.log(`Max retries reached for ${source} audio playback`);
          }
        });
    }
  };

  const dialCall = async (number) => {
    const phoneInfo = store.getters.phoneInfo;
    
    newCall.value = {
      call_type: 2,
      number: number,
      time: moment().format('YYYY-MM-DD HH:mm:ss'),
      duration: '00:00:00',
    };
    
    WebRTCService.call(number, phoneInfo.pbxServer);
  };

  const answerCall = () => {
    const isAnswered = store.getters.isAnswered;
    const agentStatusPermission = store.getters.agentStatusPermission;
    
    if (WebRTCService.getSession() && !isAnswered) {
      WebRTCService.answer();
      if (!hasPermission(agentStatusPermission)) {
        isAdminCall.value = true;
        isMinimised.value = true;
      }
    }
  };

  // Send MonitorAction(5) to release the whisper bridge on the PBX.
  // Without this the backend keeps the Local channel parked and re-INVITEs
  // the admin on next SIP registration / CheckAgentActiveCall.
  const stopWhisperIfActive = async () => {
    const whisperedAgent = store.getters.whisperedAgent;
    const whisperedChannel = store.getters.whisperedChannel;
    if (!whisperedAgent || !whisperedChannel) return;

    try {
      await store.dispatch('MonitorAction', {
        action: 5,
        channel: whisperedChannel,
        agent: whisperedAgent,
        workstatus: 1,
      });
    } catch (err) {
      console.log('Error stopping whisper:', err);
    }
  };

  const hangupCall = async () => {
    if (isAdminCall.value) {
      await stopWhisperIfActive();
    }

    if (WebRTCService.getSession()) {
      console.log('hanging up');
      WebRTCService.hangup();
      clearInterval(durationTimer.value);
    }
  };

  const holdCall = () => {
    WebRTCService.hold();
  };

  const muteCall = () => {
    WebRTCService.mute();
  };

  const setCallDuration = () => {
    durationTimer.value = setInterval(() => {
      const session = WebRTCService.getSession();
      if (!session || !newCall.value) return;

      const now = new Date().getTime();
      const startTime = new Date(session._start_time).getTime();
      const distance = now - startTime;

      const hours = Math.floor((distance % (1000 * 60 * 60 * 60)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      newCall.value.duration =
        (hours < 10 ? '0' + hours : hours) +
        ':' +
        (minutes < 10 ? '0' + minutes : minutes) +
        ':' +
        (seconds < 10 ? '0' + seconds : seconds);
    }, 1000);
  };

  const getCalls = async () => {
    try {
      const resp = await store.dispatch('GetAgentLastCalls', 18);
      if (resp && resp.success && resp.lastCalls) {
        store.commit('setCallsList', resp.lastCalls);
      }
    } catch (err) {
      console.log('Error getting calls:', err);
      let message = err.message;
      if (err.response) {
        message = err.response.data.message;
      }
      store.dispatch('SetMessageDialog', { show: true, message });
    }
  };

  const getCurrentPBXCall = async () => {
    try {
      let caller = '';
      if (sessionPeer.value) {
        caller = sessionPeer.value;
      }
      const resp = await store.dispatch('GetActiveCall', caller);
      // `result` can come back as {} (no call), so reach in defensively —
      // resp.call.Channel on an absent call threw into the catch below.
      const channel = String(resp?.call?.Channel ?? '').trim();
      if (!resp?.success || !channel) {
        if (!resp?.success) {
          console.log('Error GetCurrentPBXCall Getting Current Web Call', resp);
        }
        return;
      }
      const incoming = JSON.parse(JSON.stringify(resp.call));
      const existingCall = store.getters.currentCall;
      if (existingCall) {
        // Later payloads DROP values rather than restate them: once the call is
        // answered `Dst` comes back "" even though the call is still in the same
        // queue. A plain spread let that blank overwrite the queue we already
        // knew, and SetCallOnHold sends currentCall.Dst as `queue` — the
        // middleware rejects an empty one with "invalid call values". So only
        // fields that actually carry a value are allowed to replace what we have
        // (0 and false are values; "" / null / undefined are not).
        const merged = { ...existingCall };
        Object.entries(incoming).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            merged[key] = value;
          }
        });
        store.commit('setCurrentCall', merged);
      } else {
        store.commit('setCurrentCall', incoming);
      }
    } catch (err) {
      console.log('Error in GetCurrentPBXCall:', err);
    }
  };

  const hasPermission = (permission) => {
    const permissions = store.getters.permissions;
    return permissions && permissions.includes(permission);
  };

  const minimiseCall = () => {
    isMinimised.value = true;
  };

  const maximiseCall = () => {
    isMinimised.value = false;
  };

  const resetCallState = () => {
    store.commit('setIsAnswered', false);
    store.commit('setIsMute', false);
    store.commit('setIsHolding', false);
    store.commit('setIsIncoming', false);
    store.commit('setIsOutgoing', false);
    store.commit('setSessionPeer', null);
    store.commit('setWhisperedAgent', null); // Clear whispered agent when call ends
    store.commit('setWhisperedChannel', null); // Clear whispered channel when call ends
    clearInterval(durationTimer.value);
  };

  const cleanup = () => {
    console.log('CallManager cleanup initiated...');
    
    // Clear any pending timers
    if (durationTimer.value) {
      clearInterval(durationTimer.value);
      durationTimer.value = null;
    }
    
    // Reset call state
    resetCallState();
    
    // Reset local state
    inSession.value = false;
    phoneConnected.value = false;
    newCall.value = null;
    isMinimised.value = false;
    isAdminCall.value = false;
    
    // Perform WebRTC cleanup
    WebRTCService.cleanup();
    
    console.log('CallManager cleanup completed');
  };

  // Force reset - for complete state reset
  const forceReset = () => {
    console.log('Force resetting CallManager...');
    cleanup();
    WebRTCService.reset();
    console.log('CallManager force reset completed');
  };

  // Logout cleanup - stops all monitoring and removes everything
  const logoutCleanup = () => {
    console.log('CallManager logout cleanup initiated...');
    
    // Clear any pending timers
    if (durationTimer.value) {
      clearInterval(durationTimer.value);
      durationTimer.value = null;
    }
    
    // Reset call state
    resetCallState();
    
    // Reset local state
    inSession.value = false;
    phoneConnected.value = false;
    newCall.value = null;
    isMinimised.value = false;
    isAdminCall.value = false;
    
    // Perform WebRTC logout cleanup (removes all listeners and global refs)
    WebRTCService.logoutCleanup();
    
    console.log('CallManager logout cleanup completed');
  };

  return {
    // State
    inSession,
    phoneConnected,
    newCall,
    isMinimised,
    isAdminCall,
    
    // Computed
    sessionPeer,
    showDial,
    enableDial,
    
    // Methods
    initPhone,
    dialCall,
    answerCall,
    hangupCall,
    holdCall,
    muteCall,
    minimiseCall,
    maximiseCall,
    hasPermission,
    resetCallState,
    cleanup,
    forceReset,
    logoutCleanup, // Add logout cleanup method
    getCalls,
    getCurrentPBXCall,
    
    // Audio handling methods
    setupAudioStream,
    createAudioElement,
    playAudioWithRetry,
  };
} 